if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        // Prevent background scroll on touch devices when dragging outside modal content
        this.addEventListener(
          'touchmove',
          (e) => {
            if (!e.target.closest('.quick-add-modal__content')) {
              e.preventDefault();
            }
          },
          { passive: false }
        );
      }

      hide(preventFocus = false) {
        document.documentElement.classList.remove('overflow-hidden');
        document.body.classList.remove('overflow-hidden');
        const savedScroll = this.scrollPos;
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification && this.openedBy) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();

        if (savedScroll !== undefined && savedScroll !== null) {
          try {
            window.scrollTo({ top: savedScroll, behavior: 'instant' });
          } catch (e) {
            window.scrollTo(0, savedScroll);
          }
        }
      }

      show(opener) {
        this.scrollPos = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        document.documentElement.classList.add('overflow-hidden');
        document.body.classList.add('overflow-hidden');
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
        }
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (!productElement) {
              window.location.href = opener.getAttribute('data-product-url');
              return;
            }

            // Force all images to eager load immediately
            productElement.querySelectorAll('img').forEach((img) => {
              img.removeAttribute('loading');
              img.setAttribute('loading', 'eager');
              img.setAttribute('decoding', 'sync');
            });

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            // Re-ensure eager loading after DOM injection
            this.modalContent.querySelectorAll('img').forEach((img) => {
              img.removeAttribute('loading');
              img.setAttribute('loading', 'eager');
              img.setAttribute('decoding', 'sync');
            });

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);

            // Initialize carousel controls in modal
            setTimeout(() => {
              const viewer = this.modalContent.querySelector('[id^="GalleryViewer"]');
              if (!viewer) return;

              const slider = viewer.querySelector('.slider') || viewer.querySelector('ul');
              const slides = viewer.querySelectorAll('.product__media-item');
              const prevBtn = viewer.querySelector('.slider-button--prev');
              const nextBtn = viewer.querySelector('.slider-button--next');
              const dots = viewer.querySelectorAll('.dk-dot, .slider-counter__link');

              // Ensure images are loaded eagerly
              viewer.querySelectorAll('img').forEach((img) => {
                img.removeAttribute('loading');
                img.setAttribute('loading', 'eager');
                img.setAttribute('decoding', 'sync');
              });

              if (!slider || slides.length < 2) {
                if (prevBtn) prevBtn.style.display = 'none';
                if (nextBtn) nextBtn.style.display = 'none';
                const counter = viewer.querySelector('.slider-counter');
                if (counter) counter.style.display = 'none';
                return;
              }

              let currentIndex = 0;

              const goToSlide = (index) => {
                currentIndex = Math.max(0, Math.min(index, slides.length - 1));
                const targetSlide = slides[currentIndex];
                if (targetSlide) {
                  slider.scrollTo({
                    left: targetSlide.offsetLeft,
                    behavior: 'smooth'
                  });
                }
                updateUI();
              };

              const updateUI = () => {
                dots.forEach((dot, i) => {
                  dot.classList.toggle('dk-dot--active', i === currentIndex);
                  dot.classList.toggle('slider-counter__link--active', i === currentIndex);
                });
                if (prevBtn) prevBtn.disabled = currentIndex === 0;
                if (nextBtn) nextBtn.disabled = currentIndex === slides.length - 1;
              };

              if (prevBtn) {
                prevBtn.onclick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToSlide(currentIndex - 1);
                };
              }

              if (nextBtn) {
                nextBtn.onclick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToSlide(currentIndex + 1);
                };
              }

              dots.forEach((dot, index) => {
                dot.onclick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToSlide(index);
                };
              });

              slider.addEventListener(
                'scroll',
                () => {
                  const scrollLeft = slider.scrollLeft;
                  const slideWidth = slides[0]?.offsetWidth || 1;
                  const newIndex = Math.round(scrollLeft / slideWidth);
                  if (newIndex !== currentIndex) {
                    currentIndex = newIndex;
                    updateUI();
                  }
                },
                { passive: true }
              );

              updateUI();
            }, 60);
          })
          .catch((err) => {
            console.error('Quick add error:', err);
            window.location.href = opener.getAttribute('data-product-url');
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            const spinner = opener.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Remove elements requested to be hidden in quick add
        productElement.querySelectorAll('.pin-code-checker').forEach((el) => el.remove());
        productElement.querySelectorAll('[class*="product-checkout-badges"]').forEach((el) => el.remove());
        productElement.querySelectorAll('.dk-prepaid-offer').forEach((el) => el.remove());
        productElement.querySelectorAll('.product__view-details').forEach((el) => el.remove());
        productElement.querySelectorAll('.product-mobile-top-nav').forEach((el) => el.remove());
        productElement.querySelectorAll('.dk-title-actions, .dk-share-btn').forEach((el) => el.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const mediaImages = productElement.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        mediaImages.forEach((img) => {
          img.removeAttribute('loading');
          img.setAttribute('loading', 'eager');
          img.setAttribute('decoding', 'sync');
        });

        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
